
import React, { Component } from "react";
import "./index.css";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

/* Actions */
import { updateUserProfile } from "../../../../redux/actions/user";
import { adjustProfileImage } from "../../../../redux/actions/misc";

/* Constants */
import { spokenLanguages, countries, nationalities, candidateDetailsProps } from "../../../../constants";

/* Components */
import DashboardSectionBase from "../../../../components/DashboardSectionBase";
import EditPhoto from "../../../../components/EditPhoto";
import RegularTextInput from "../../../../components/RegularTextInput";
import RegularToggleButton from "../../../../components/RegularToggleButton";
import TextInputDropdown from "../../../../components/TextInputDropdown";
import SelectedItems from "../../../../components/SelectedItems";
import TextArea from "../../../../components/TextArea";
import CheckBox from "../../../../components/CheckBox";
import SaveButton from "../../../../components/SaveButton";

// Canonicalise nationality entries so legacy CV-parse data (UK, Great
// Britain, United Kingdom) collapses into a single chip when displayed.
// Doesn't touch the stored data - just the render. NB: kept distinct
// English/Scottish/Welsh - they are separate nationalities.
const NATIONALITY_ALIASES = {
    "uk": "British",
    "u.k.": "British",
    "great britain": "British",
    "united kingdom": "British",
};

const dedupeNationality = (items) => {
    if (!Array.isArray(items)) return items;
    const seen = new Set();
    const out = [];
    items.forEach((item) => {
        if (typeof item !== "string") {
            out.push(item);
            return;
        }
        const canonical = NATIONALITY_ALIASES[item.trim().toLowerCase()] || item;
        const key = canonical.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        out.push(canonical);
    });
    return out;
};

const QuickPicks = ({ options, selected, onPick }) => {
    const filtered = options.filter((o) => !(selected || []).map((s) => (typeof s === "string" ? s.toLowerCase() : "")).includes(o.toLowerCase()));
    if (filtered.length === 0) return null;
    return (
        <div className="quick-picks">
            <span className="quick-picks-label">Quick add</span>
            {filtered.map((opt) => (
                <button key={opt} type="button" className="quick-picks-chip" onClick={() => onPick(opt)}>
                    + {opt}
                </button>
            ))}
        </div>
    );
};

class Details extends Component {

    constructor(props) {
        super(props);

        this.state = {
            spokenLanguage: "",
            nationality: "",
        };

    }

    componentDidMount() {

        return document.getElementById("Dashboard").scrollTo(0, 0);

    }

    validateProfile = (profile) => {
        const errors = [];
        const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
        const phoneRegex = /^[+]?[\d\s().-]{7,}$/;

        if (profile.dob && profile.dob.length >= 10 && !dateRegex.test(profile.dob)) {
            errors.push("Date of birth should be in DD/MM/YYYY format");
        }
        if (profile.height && !/^\d+(\.\d+)?$/.test(String(profile.height).trim())) {
            errors.push("Height should be a number (in centimetres)");
        }
        if (profile.primaryPhone && profile.primaryPhone.length >= 7 && !phoneRegex.test(profile.primaryPhone)) {
            errors.push("Primary phone number doesn't look valid");
        }
        if (profile.secondaryPhone && profile.secondaryPhone.length >= 7 && !phoneRegex.test(profile.secondaryPhone)) {
            errors.push("Secondary phone number doesn't look valid");
        }
        const visaFields = [
            { obj: profile.b1b2, label: "B1/B2" },
            { obj: profile.c1d, label: "C1/D" },
            { obj: profile.schengen, label: "Schengen" },
            { obj: profile.greenCard, label: "Green Card" },
        ];
        visaFields.forEach(({ obj, label }) => {
            if (obj?.status && obj?.expiryDate && obj.expiryDate.length >= 10 && !dateRegex.test(obj.expiryDate)) {
                errors.push(`${label} expiry date should be in DD/MM/YYYY format`);
            }
        });

        return errors;
    }

    render() {

        const { profile } = this.props.user.data;
        const errors = this.validateProfile(profile);

        return(
            <>
                {errors.length > 0 &&
                    <div className="details-error-summary">
                        <p className="details-error-summary-title">{errors.length === 1 ? "1 issue to fix" : `${errors.length} issues to fix`}</p>
                        <ul className="details-error-summary-list">
                            {errors.map((e, i) => <li key={i}>{e}</li>)}
                        </ul>
                    </div>
                }
                <DashboardSectionBase number="01" title={"Profile Photo"} subtitle="A clear shot of you. First impression for captains.">
                    <div className="details-photo-section">
                        <EditPhoto profileImage={profile?.profileImage} editPhoto={() => this.props.adjustProfileImage(true)} size={120} />
                        <div className="details-photo-meta">
                            <p className="details-photo-meta-hint">{profile?.profileImage ? "Tap the photo to change it." : "Tap the camera badge to upload."}</p>
                            {profile?.profileImage &&
                                <button type="button" className="details-photo-remove-link" onClick={() => this.props.updateUserProfile([{ propName: "profileImage", value: null }])}>Remove photo</button>
                            }
                        </div>
                    </div>
                    <ul className="details-photo-tips">
                        <li>Square crops work best.</li>
                        <li>Light backgrounds, no sunglasses.</li>
                        <li>Smiling helps more than you'd think.</li>
                    </ul>
                </DashboardSectionBase>
                <DashboardSectionBase number="02" title={"Personal Details"} subtitle="The essentials about you.">
                    <div className="margin-top-2x">
                        <RegularTextInput label={"Date of birth"} placeholder="DD/MM/YYYY" value={profile.dob || ""} propName={"dob"} onChangeText={this.props.updateUserProfile} profile={profile} isDate={true} />
                    </div>
                    <div className="margin-top-20px">
                        <RegularToggleButton label={"Gender"} propName={"gender"} options={[{ name: "Male" }, { name: "Female" }]} toggle={this.props.updateUserProfile} profile={profile} />
                    </div>
                    <div className="margin-top-20px">
                        <RegularTextInput label={"Height"} placeholder="e.g. 180" value={profile.height || ""} propName={"height"} unit={"centimetres"} onChangeText={this.props.updateUserProfile} profile={profile} />
                    </div>
                    <div className="margin-top-20px detail-field-group">
                        <TextInputDropdown label={"Languages Spoken"} placeholder="Search and add a language" value={this.state.spokenLanguage} propName={"languages"} values={profile.languages} options={spokenLanguages} onChangeText={(e) => this.setState({ spokenLanguage: e })} select={this.props.updateUserProfile} />
                        <SelectedItems items={profile.languages} propName={"languages"} updateUserProfile={this.props.updateUserProfile} />
                        <QuickPicks
                            options={["English", "Spanish", "French", "Italian", "German", "Portuguese", "Greek"]}
                            selected={profile.languages}
                            onPick={(val) => this.props.updateUserProfile([{ propName: "languages", value: [...(profile.languages || []), val] }], true)}
                        />
                    </div>
                    <div className="margin-top-20px detail-field-group">
                        <TextInputDropdown label={"Nationality"} placeholder="Search and add a nationality" value={this.state.nationality} values={profile.nationality} options={nationalities} onChangeText={(e) => this.setState({ nationality: e })} propName={"nationality"} select={this.props.updateUserProfile} />
                        <SelectedItems items={dedupeNationality(profile.nationality)} propName={"nationality"} updateUserProfile={this.props.updateUserProfile} />
                        <QuickPicks
                            options={["British", "Australian", "American", "South African", "New Zealander", "French", "Italian", "Spanish"]}
                            selected={profile.nationality}
                            onPick={(val) => this.props.updateUserProfile([{ propName: "nationality", value: [...(profile.nationality || []), val] }], true)}
                        />
                    </div>
                    <div className="margin-top-20px">
                        <TextInputDropdown label={"Country of Residence"} placeholder="Search for your country" value={profile.country} values={[profile.country]} options={countries} onChangeText={(e) => this.props.updateUserProfile([{ propName: "country", value: e }])} propName={"country"} select={this.props.updateUserProfile} single={true} />
                    </div>
                    <div className="margin-top-20px">
                        <RegularToggleButton label={"Are you a smoker?"} propName={"smoking"} options={[{ name: "Non Smoker", bool: false }, { name: "Smoker", bool: true }]} toggle={this.props.updateUserProfile} profile={profile} />
                    </div>
                    <div className="margin-top-20px">
                        <RegularToggleButton label={"Do you have tattoos?"} propName={"tattoo"} options={[{ name: "No Tattoos" }, { name: "No Visible Tattoos" }, { name: "Visible Tattoos" }]} toggle={this.props.updateUserProfile} profile={profile} />
                    </div>
                    <div className="margin-top-20px">
                        <RegularToggleButton label={"Relationship Status"} propName={"relationshipStatus"} options={[{ name: "Married" }, { name: "Single" }, { name: "In a Relationship" }]} toggle={this.props.updateUserProfile} profile={profile} />
                    </div>
                </DashboardSectionBase>
                <DashboardSectionBase number="03" title={"Visas Held"} subtitle="Let us know which visas you hold.">
                    <div className="margin-top-20px">
                        <CheckBox label={"B1/B2"} status={profile?.b1b2?.status} propName={"b1b2"} value={profile?.b1b2} toggle={this.props.updateUserProfile} profile={profile} />
                        {profile?.b1b2?.status &&
                            <div className="margin-top-1x">
                                <RegularTextInput label={"Expiry Date"} placeholder="DD/MM/YYYY" value={profile?.b1b2?.expiryDate || ""} onChangeText={this.props.updateUserProfile} profile={profile} propName={"b1b2"} isDate={true} />
                            </div>
                        }
                    </div>
                    <div className="margin-top-20px">
                        <CheckBox label={"C1/D"} status={profile?.c1d?.status} propName={"c1d"} value={profile?.c1d} toggle={this.props.updateUserProfile} profile={profile} />
                        {profile?.c1d?.status &&
                            <div className="margin-top-1x">
                                <RegularTextInput label={"Expiry Date"} placeholder="DD/MM/YYYY" value={profile?.c1d?.expiryDate || ""} onChangeText={this.props.updateUserProfile} profile={profile} propName={"c1d"} isDate={true} />
                            </div>
                        }
                    </div>
                    <div className="margin-top-20px">
                        <CheckBox label={"Schengen"} status={profile?.schengen?.status} propName={"schengen"} value={profile?.schengen} toggle={this.props.updateUserProfile} profile={profile} />
                        {profile?.schengen?.status &&
                            <div className="margin-top-1x">
                                <RegularTextInput label={"Expiry Date"} placeholder="DD/MM/YYYY" value={profile?.schengen?.expiryDate || ""} onChangeText={this.props.updateUserProfile} profile={profile} propName={"schengen"} isDate={true} />
                            </div>
                        }
                    </div>
                    <div className="margin-top-20px">
                        <CheckBox label={"Green Card"} status={profile?.greenCard?.status} propName={"greenCard"} value={profile?.greenCard} toggle={this.props.updateUserProfile} profile={profile} />
                        {profile?.greenCard?.status &&
                            <div className="margin-top-1x">
                                <RegularTextInput label={"Expiry Date"} placeholder="DD/MM/YYYY" value={profile?.greenCard?.expiryDate || ""} onChangeText={this.props.updateUserProfile} profile={profile} propName={"greenCard"} isDate={true} />
                            </div>
                        }
                    </div>
                </DashboardSectionBase>
                <DashboardSectionBase number="04" title={"Contact Details"} subtitle="How we reach you when a job comes up.">
                    <div className="contact-details-container margin-top-20px">
                        <RegularTextInput label={"Phone Number"} placeholder="e.g. +44 7700 900123" value={profile.primaryPhone || ""} propName={"primaryPhone"} onChangeText={this.props.updateUserProfile} profile={profile} />
                        <div className="contact-details-checkbox-container">
                            <CheckBox label={"WhatsApp"} icon={require("../../../../assets/svgs/whatsapp.svg")} status={profile.primaryPhoneWhatsApp} propName={"primaryPhoneWhatsApp"} value={!profile.primaryPhoneWhatsApp} toggle={this.props.updateUserProfile} profile={profile} />
                        </div>
                    </div>
                    <div className="contact-details-container margin-top-20px">
                        <RegularTextInput label={"Other Phone Number (Optional)"} placeholder="e.g. +44 7700 900123" value={profile.secondaryPhone || ""} propName={"secondaryPhone"} onChangeText={this.props.updateUserProfile} profile={profile} />
                        <div className="contact-details-checkbox-container">
                            <CheckBox label={"WhatsApp"} icon={require("../../../../assets/svgs/whatsapp.svg")} status={profile.secondaryPhoneWhatsApp} value={!profile.secondaryPhoneWhatsApp} propName={"secondaryPhoneWhatsApp"} toggle={this.props.updateUserProfile} profile={profile} />
                        </div>
                    </div>
                </DashboardSectionBase>
                <DashboardSectionBase number="05" title={"Profile Summary"} subtitle="Tell us more about yourself. What makes you a great asset onboard?">
                    <div className="margin-top-20px">
                        <TextArea value={profile.aboutMe} propName={"aboutMe"} placeholder={"Start Typing..."} onChangeText={this.props.updateUserProfile} />
                    </div>
                </DashboardSectionBase>
                <DashboardSectionBase number="06" title={"Hobbies & Interests"} subtitle="Let us know how you like to spend your spare time.">
                    <div className="margin-top-20px">
                        <TextArea value={profile.hobbies} propName={"hobbies"} placeholder={"Start Typing..."} onChangeText={this.props.updateUserProfile} />
                    </div>
                </DashboardSectionBase>
                <SaveButton propNames={candidateDetailsProps} profile={profile} updateUserProfile={this.props.updateUserProfile} next="/profile/ideal-job" history={this.props.history} />
            </>
        );

    }

}

const mapStateToProps = (state) => {
    return {
        user: state.user,
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        ...bindActionCreators({ updateUserProfile, adjustProfileImage }, dispatch)
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(Details);